<script setup>
import { decode } from "html-entities";
import { load } from "cheerio";
import {
	ReferencedStocks,
	ReferencedStocksSidebar
} from "@stockstotrade_com/stt-ts-shared-lib";
import {
	extractStockSymbol,
	processHeadingsForReferencedStocks,
	STOCK_TICKER_REGEX
} from "~/utils/referenced-stocks-content";
import { useStocksStore } from "~/stores/stocks";
const stocksStore = useStocksStore();

const hasTwitterEmbedd = ref(false);
const showBeyondWords = ref(false);
const content = ref();
const $config = useRuntimeConfig();
const route = useRoute();
const category = ref(null);
const comments = ref([]);
const tags = ref(null);
const commentsCount = ref(null);
const stockSymbols = ref([]);
const adPlacement = ref();
const aizenSidebarAd = useState(
	"aizen-ad-c26b2507-1c06-43a3-bf84-32efaf76127e",
	() => null
);

store.toggleLoading();

const { current_hostname } = useRuntimeConfig().public;

definePageMeta({
	key: (route) => route.fullPath
});

// Create reactive computed properties to maintain API compatibility
const stocks_info = {
	stocks: computed(() => stocksStore.stocks)
};

watch(
	stockSymbols,
	async (newSymbols) => {
		if (process.client) {
			if (!newSymbols || newSymbols.length === 0) {
				stocksStore.stopTrackingSymbols();
				return;
			}

			const symbols = newSymbols
				.filter(Boolean)
				.map((symbol) => symbol.toUpperCase());

			await stocksStore.startTrackingSymbols(symbols);
		}
	},
	{
		deep: true,
		immediate: true
	}
);

const stocks = computed(() => stocks_info.stocks.value);

const stocks_ytd = computed(() => {
	const allStocks = stocks_info.stocks.value;
	if (!allStocks || typeof allStocks !== "object") return {};

	return Object.fromEntries(
		Object.entries(allStocks).filter(([, stock]) => stock?.NAME && stock?.SYMBOL)
	);
});

const schemaContentParser = (content) => {
	const imageList = [];
	const $ = load(content, {
		decodeEntities: false // Prevent HTML entity decoding
	});

	$("img").each((_index, el) => {
		imageList.push({
			"@type": "ImageObject",
			url: el.attribs.src,
			width: el.attribs.width,
			height: el.attribs.height
		});
	});

	$('[data-beyondwords-player="true"], script, style, img').remove();
	// Extract text content
	const textContent = $("body").text();

	return { textContent, imageList };
};

const schemaMaker = (
	post,
	current_hostname,
	rating,
	faq_code,
	all_authors = []
) => {
	const payload = [];
	const author = all_authors.find(
		(author) =>
			author.name === post.author.name ||
			(author.name === "Tim Sykes" && post.author.name === "Timothy Sykes")
	);

	let social_links;

	if (author && author.custom_sections) {
		author.custom_sections = isValidJSON(author.custom_sections)
			? JSON.parse(author.custom_sections)
			: author.custom_sections;

		social_links = author.custom_sections
			.find((section) => section.name === "Social")
			?.values.map((item) => item.value);
	}

	const article = {
		"@context": "https://schema.org",
		"@id": current_hostname + "blog/" + post.slug + "/#Article",
		url: current_hostname + "blog/" + post.slug + "/",
		inLanguage: "en-US",
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": current_hostname + "blog/" + post.slug + "/"
		},
		headline: post.title,
		description: post.yoast_head_json.description
	};
	const category = post.categories;

	const breadcrumb_list = {
		"@type": "BreadcrumbList",
		"@id": current_hostname + "blog/" + post.slug + "/#breadcrumb",
		"@context": "https://schema.org",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				item: {
					"@id": current_hostname,
					name: "Timothy Sykes"
				}
			},
			{
				"@type": "ListItem",
				position: 2,
				item: {
					"@id": current_hostname + "category/" + category[0].slug + "/",
					name: category[0].name
				}
			},
			{
				"@type": "ListItem",
				position: 3,
				item: {
					"@id": current_hostname + "blog/" + post.slug + "/",
					name: post.title
				}
			}
		]
	};

	payload.push(breadcrumb_list);

	const featuredImage = {
		"@type": "ImageObject",
		"@id": current_hostname + "blog/" + post.slug + "/#primaryimage",
		url:
			post._embedded["wp:featuredmedia"] && post._embedded["wp:featuredmedia"][0]
				? post._embedded["wp:featuredmedia"][0].source_url
				: "",
		width:
			post._embedded["wp:featuredmedia"] &&
			post._embedded["wp:featuredmedia"][0] &&
			post._embedded["wp:featuredmedia"][0].media_details &&
			post._embedded["wp:featuredmedia"][0].media_details.width
				? post._embedded["wp:featuredmedia"][0].media_details.width
				: "",
		height:
			post._embedded["wp:featuredmedia"] &&
			post._embedded["wp:featuredmedia"][0] &&
			post._embedded["wp:featuredmedia"][0].media_details &&
			post._embedded["wp:featuredmedia"][0].media_details.height
				? post._embedded["wp:featuredmedia"][0].media_details.height
				: ""
	};

	const parsedStuff = schemaContentParser(post.content);

	if (post.tags && post.tags.length > 0) {
		article.keywords = post.tags[0].name + ",";
	}

	const schema_parse = JSON.parse(post.yoast_head_json.schema_json);

	const primaryImageFromSchema = schema_parse["@graph"].find(
		(item) =>
			item["@type"] === "ImageObject" && item["@id"]?.includes("#primaryimage")
	);

	if (primaryImageFromSchema) {
		featuredImage.url =
			primaryImageFromSchema.contentUrl ||
			primaryImageFromSchema.url ||
			featuredImage.url;
		featuredImage.width = primaryImageFromSchema.width || featuredImage.width;
		featuredImage.height = primaryImageFromSchema.height || featuredImage.height;
	}

	const datesFromSchema = schema_parse["@graph"].find(
		(item) => item["@type"] === "Article"
	);

	const timAuthor = {
		"@type": "Person",
		name: "Timothy Sykes",
		url: current_hostname + "blog/author/timothysykes/",
		description: "",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};
	const mattMonaco = {
		"@type": "Person",
		name: "Matt Monaco",
		url: current_hostname + "blog/author/mattmonaco/",
		description: "",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/f47e982e112566f6e63ad14aa64d1829?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};

	const timBot = {
		"@type": "Person",
		name: "Tim bot",
		url: current_hostname + "blog/author/tim-bot/",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/341a364cbdd97f47f09948e5ca2e682e?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};

	const printEditor = (author = 1) => {
		if (author === 1) {
			return {
				...timAuthor,
				description: "",
				sameAs: [current_hostname + "/blog/author/timothysykes/", "timothysykes"]
			};
		}

		if (author === 6500) {
			return {
				...timBot
				// sameAs: [current_hostname + "/blog/author/tim-bot/", "tim-bot"]
			};
		}
		if (author === 6486) {
			return {
				...mattMonaco
				// sameAs: [current_hostname + "/blog/author/tim-bot/", "tim-bot"]
			};
		}

		return {
			...timAuthor,
			description: "",
			sameAs: [current_hostname + "/blog/author/timothysykes/", "timothysykes"]
		};
	};

	if (post.reviewed_by || post.fact_checked_by) {
		article.mainEntityOfPage.reviewedBy = [];

		const reviewed_by = all_authors.find(
			(author) =>
				author.name === post.reviewed_by ||
				(author.name === "Tim Sykes" && post.reviewed_by === "Timothy Sykes")
		);

		const fact_checked_by = all_authors.find(
			(author) =>
				author.name === post.fact_checked_by ||
				(author.name === "Tim Sykes" && post.fact_checked_by === "Timothy Sykes")
		);

		if (reviewed_by) {
			reviewed_by.custom_sections = isValidJSON(reviewed_by.custom_sections)
				? JSON.parse(reviewed_by.custom_sections)
				: reviewed_by.custom_sections;

			reviewed_by.social_links =
				reviewed_by.custom_sections
					.find((section) => section.name === "Social")
					?.values.map((item) => item.value) || [];

			article.mainEntityOfPage.reviewedBy.push({
				"@type": "Person",
				name: reviewed_by.name,
				url:
					current_hostname +
					"blog/author/" +
					reviewed_by.name.replace(/ /g, "-").toLowerCase() +
					"/",
				sameAs: reviewed_by.social_links.find(
					(item) =>
						item.includes("twitter") ||
						item.includes("Twitter") ||
						item.includes("x") ||
						item.includes("X")
				)
			});
		}

		if (fact_checked_by) {
			fact_checked_by.custom_sections = isValidJSON(
				fact_checked_by.custom_sections
			)
				? JSON.parse(fact_checked_by.custom_sections)
				: fact_checked_by.custom_sections;

			fact_checked_by.social_links =
				fact_checked_by.custom_sections
					.find((section) => section.name === "Social")
					?.values.map((item) => item.value) || [];

			article.mainEntityOfPage.reviewedBy.push({
				"@type": "Person",
				name: fact_checked_by.name,
				url:
					current_hostname +
					"blog/author/" +
					fact_checked_by.name.replace(/ /g, "-").toLowerCase() +
					"/",
				sameAs: fact_checked_by.social_links.find(
					(item) =>
						item.includes("twitter") ||
						item.includes("Twitter") ||
						item.includes("x") ||
						item.includes("X")
				)
			});
		}
	}

	const publisher = {
		"@type": "Organization",
		name: "Timothy Sykes",
		url: current_hostname,
		image: {
			"@type": "ImageObject",
			url: "https://content.timothysykes.com/wp-content/uploads/2020/10/timothy-logo-160-50.png",
			height: 160,
			width: 50
		}
	};

	// article.mainEntityOfPage.reviewedBy = reviewers;

	article.datePublished = datesFromSchema.datePublished;
	article.dateModified = datesFromSchema.dateModified;
	article.author = {
		"@type": "Person",
		name: author?.name || "Timothy Sykes",
		url: `https://www.timothysykes.com/blog/author/${
			author && author.name
				? author.name.toLowerCase().replace(/ /g, "-")
				: "tim-sykes"
		}/`,
		sameAs: social_links || [
			"https://www.facebook.com/timsykesfans/",
			"https://twitter.com/timothysykes",
			"https://www.linkedin.com/in/timothysykes/",
			"https://www.instagram.com/timothysykes/",
			"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng"
		],
		image: [
			{
				"@type": "ImageObject",
				url:
					author?.profile_image ||
					"https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp",
				width: 306,
				height: 384
			},
			{
				"@type": "ImageObject",
				url:
					author?.profile_image ||
					"https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
				height: 96,
				width: 96
			}
		],
		jobTitle: "Penny Stock Trader and Mentor",
		publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/",
		affiliation: {
			"@type": "Organization",
			name: "Timothy Sykes",
			sameAs: "https://www.timothysykes.com"
		}
	};

	article.editor = printEditor(post.author);
	article.publisher = publisher;

	if (
		post._embedded["wp:featuredmedia"] &&
		post._embedded["wp:featuredmedia"][0]
	) {
		article.image = [featuredImage, ...parsedStuff.imageList];
	} else {
		article.image = [...parsedStuff.imageList];
	}

	payload.push({ "@type": "Article", ...article });

	if ([118, 3].includes(post.categories[0])) {
		const youtubeStuff = getYoutubeLink(post.content);
		const videoObject = {
			"@context": "https://schema.org",
			"@type": "VideoObject",
			"@id": current_hostname + "blog/" + post.slug + "/#videoobject",
			url: current_hostname + "blog/" + post.slug + "/",
			headline: post.title,
			datePublished: datesFromSchema.datePublished,
			dateModified: datesFromSchema.dateModified,
			description: post.yoast_head_json.description,
			transcript: parsedStuff.textContent,
			name: post.title,
			uploadDate: datesFromSchema.datePublished,
			thumbnailUrl: youtubeStuff ? youtubeStuff.thumbnailUrl : "",
			contentUrl: youtubeStuff ? youtubeStuff.contentUrl : "",
			embedUrl: youtubeStuff ? youtubeStuff.embedUrl : "",
			author: timAuthor,
			publisher,
			image: [featuredImage, ...parsedStuff.imageList]
		};

		if (rating && (rating.post_count > 0 || rating.rating > 0)) {
			videoObject.aggregateRating = {
				"@type": "AggregateRating",
				bestRating: rating.best_rating || 5,
				ratingCount: rating.post_count,
				ratingValue: rating.rating
			};
		}

		payload.push(videoObject);
	}

	if (faq_code) {
		payload.push({
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faq_code.questions.map((question) => ({
				"@type": "Question",
				name: question.title,
				acceptedAnswer: {
					"@type": "Answer",
					text: question.answer
				}
			}))
		});
	}

	return [
		{
			"@context": "https://schema.org"
		},
		...payload
	];
};

const fetchPostIdGraphQL = async (hostname, slug, env) => {
	try {
		const { data } = await $fetch(hostname + "graphql", {
			method: "post",
			body: {
				query: `
				query NewQuery {
					postBy(slug: "/blog/${slug}/") {
						title
						postId
					}
				}`
			}
		});

		if (data && data.postBy) {
			return { id: data.postBy.postId };
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		if (env !== "production") console.error("Fetching from graphql", e);
	}
};
const fetchLeftoverInformation = async () => {
	try {
		// Related posts / tags / comments — same data WordPress GraphQL always
		// returned, but proxied through a server-cached endpoint. The WP origin
		// itself is slow for this query (30-45s observed), so we cache the
		// result there instead of paying that cost on every page view.
		const data = await $fetch("/api/dictionary/blog-extras", {
			params: { slug: route.path }
		});

		if (data && data.postBy) {
			category.value = data.postBy.categories.nodes[0];
			comments.value = data.postBy.comments.nodes;
			tags.value = data.postBy.tags.nodes;
			commentsCount.value = data.postBy.commentsCount;
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};

// Use unique key per route for proper SSR hydration
const routeSlug = route.params.slug?.[0] || "index";
const { data, error: fetchError } = await useAsyncData(
	`blog_post_data_${routeSlug}`,
	async () => {
		try {
			const params = route.params;

			if (route.name !== "blog-slug" && params.widgetpage) {
				await navigateTo("/" + params.widgetpage[0] + "/");
				return { redirected: true, url: params.widgetpage[0] };
			}

			const payload = {
				post: null,
				beyondwords: null,
				metadata: null,
				comments: null,
				schema_json: null,
				news_list: []
			};

			const routePath = route.path;

			if (
				routePath.startsWith("/.well-known") ||
				routePath.includes("favicon.ico")
			) {
				return payload;
			}
			const SLUG = params.slug?.[0] || route.params.slug?.[0];

			// Per-slug payload comes straight from S3 (ts-posts/posts/<slug>.json)
			// via /api/dictionary/posts. The S3 file is the FULL post — content,
			// yoast_head_json, pre-resolved author_info, tags and categories. No
			// WordPress REST round-trip is needed.
			let post_data;
			try {
				post_data = await $fetch("/api/dictionary/posts", {
					params: { slug: SLUG }
				});
			} catch (err) {
				// Fall back to the GraphQL ID lookup so legacy slugs that haven't been
				// backfilled to S3 yet still resolve — they just won't render full
				// content. We surface the 404 the same way the old flow did.
				if (err?.statusCode !== 404) {
					console.warn("[blog SSR] dictionary lookup failed:", err?.message);
				}
				const fallback = await fetchPostIdGraphQL(
					$config.public.hostname,
					SLUG,
					$config.public.env
				);
				if (!fallback?.id) {
					throw createError({
						statusCode: 404,
						statusMessage: "No post found",
						fatal: true
					});
				}
				throw createError({
					statusCode: 404,
					statusMessage: "No post content cached for this slug",
					fatal: true
				});
			}

			// News recommendations are non-critical — don't let failure crash SSR
			try {
				const news_recommendations = await $fetch(
					`/api/dictionary/news?recommendations=true`
				);
				payload.news_list = news_recommendations;
			} catch (e) {
				console.warn("[blog SSR] news recommendations fetch failed:", e.message);
				payload.news_list = [];
			}

			const response = normalizeS3PostToWpShape(post_data);

			// Author resolution: the S3 payload already carries the resolved
			// `author_info`. We still pull /api/authors so schemaMaker can look up
			// reviewed_by / fact_checked_by and surface their social links.
			const all_authors = await $fetch("/api/authors").catch((e) => {
				console.warn("[blog SSR] authors fetch failed:", e?.message);
				return [];
			});

			payload.post = response;

			const fetched_author = response.author_info || {
				id: response.author,
				name: "Timothy Sykes",
				profile_image:
					"https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp"
			};

			if (response.reviewed_by) {
				payload.authorship = {
					...payload.authorship,
					reviewer: { name: response.reviewed_by ?? "" }
				};
			}

			if (response.fact_checked_by) {
				payload.authorship = {
					...payload.authorship,
					factChecker: { name: response.fact_checked_by ?? "" }
				};
			}

			if (response.meta?.beyondwords_podcast_id) {
				const beyondwords_res = await fetchBeyondWords({
					podcastId: response.meta.beyondwords_podcast_id
				});

				if (beyondwords_res) {
					payload.beyondwords = beyondwords_res;
				}
			}

			// Tags + categories are pre-resolved on the S3 payload as full term
			// objects ({ id, name, slug, ... }) — no per-tag/per-category lookup
			// needed.
			const tags = Array.isArray(response.tags) ? response.tags : [];
			const categories = Array.isArray(response.categories)
				? response.categories
				: [];

			payload.post = {
				...payload.post,
				tags,
				categories,
				author: {
					id: fetched_author.id,
					name:
						fetched_author.name === "Tim Sykes"
							? "Timothy Sykes"
							: fetched_author.name,
					avatar: fetched_author.profile_image
				}
			};

			let rating;
			const firstCategoryId =
				categories.length > 0
					? typeof categories[0] === "object"
						? categories[0].id
						: categories[0]
					: null;

			// Ratings — non-critical, don't let failure crash SSR
			if ([118, 3].includes(firstCategoryId)) {
				try {
					rating = await getPostRatings(
						response.id,
						$config.public.ratings_url,
						$config.public.ratings_key
					);
				} catch (e) {
					console.warn("[blog SSR] ratings fetch failed:", e.message);
				}
			}

			// FAQ — non-critical, don't let failure crash SSR
			let faqResponse = null;
			if ($config.public.env === "production") {
				try {
					faqResponse = await $fetch("/api/faq", {
						params: {
							page: `/blog/${SLUG}/`
						}
					});
				} catch (e) {
					console.warn("[blog SSR] FAQ fetch failed:", e.message);
				}
			}

			const schema = schemaMaker(
				payload.post,
				$config.public.current_hostname,
				rating,
				faqResponse && faqResponse.body !== "no-faq" ? faqResponse : null,
				all_authors
			);

			if (faqResponse && faqResponse.body && faqResponse.body !== "no-faq") {
				payload.faq = faqResponse;
			}

			payload.schema_json = JSON.stringify(schema);

			return { ...payload };
		} catch (e) {
			console.log("ERROR:", e);

			throw createError({
				statusCode: e.statusCode || 500,
				statusMessage:
					e.statusMessage || e.message || "An error occurred during fetch",
				fatal: true
			});
		}
	},
	{
		watch: [() => route.path],
		getCachedData(key, nuxtApp) {
			// First check standard payload cache
			const cached = nuxtApp.payload.data[key] ?? nuxtApp.static?.data?.[key];
			if (cached) return cached;

			// During hydration, payload.data may be empty (known Nuxt 3 behavior).
			// Fall back to useState (stored in payload.state) to prevent unnecessary client re-fetch.
			if (nuxtApp.isHydrating && process.client) {
				const slug = route.params.slug?.[0] || "index";
				const stateKey = `$sssr_blog_${slug}`;
				const ssrState = nuxtApp.payload?.state?.[stateKey];
				if (ssrState) return ssrState;
			}

			return undefined;
		}
	}
);

// If useAsyncData captured an error (e.g. 404 from API), throw it at the script setup
// level so Nuxt's error handler triggers error.vue with the correct HTTP status code.
if (fetchError.value) {
	throw createError({
		statusCode: fetchError.value.statusCode || 404,
		statusMessage: fetchError.value.statusMessage || "Page Not Found",
		fatal: true
	});
}

// Bridge SSR data to client via useState (payload.data is empty during hydration,
// but payload.state used by useState IS available — confirmed via debug logs)
const ssrData = useState(`ssr_blog_${routeSlug}`, () => data.value ?? null);

// Keep ssrData updated when useAsyncData resolves on client
watch(
	() => data.value,
	(newData) => {
		if (newData) ssrData.value = newData;
	},
	{ immediate: false }
);

// Use computed properties with useState fallback for hydration
const post = computed(() => data.value?.post ?? ssrData.value?.post ?? null);
const beyondwords = computed(
	() => data.value?.beyondwords ?? ssrData.value?.beyondwords ?? null
);
const schema_json = computed(
	() => data.value?.schema_json ?? ssrData.value?.schema_json ?? null
);
const authorship = computed(
	() => data.value?.authorship ?? ssrData.value?.authorship ?? null
);
const faq = computed(() => data.value?.faq ?? ssrData.value?.faq ?? null);
const news_list = computed(
	() => data.value?.news_list ?? ssrData.value?.news_list ?? []
);

// isLoading based purely on data availability (post has useState fallback for hydration)
// Don't check status.value — during re-fetch after hydration, useState still provides data
const isLoading = computed(() => !post.value);

// Watch for post data to become available (handles direct page loads)
// Retry mechanism: checks every 200ms up to 4 times for sidebar tags
const sidebarInitialized = ref(false);
watch(
	() => collectSidebarTagIds(post.value),
	async (ids) => {
		if (!process.client) return;

		if (!ids.length) {
			if (store.contentSidebar.show) {
				store.toggleContentSidebarVisibility(false);
			}
			return;
		}

		if (!sidebarInitialized.value) {
			sidebarInitialized.value = true;
			await useSidebarSorter(ids);
		}
	},
	{ immediate: true }
);

const applyDatePlaceholders = (value) =>
	typeof value === "string"
		? value
				.replace(/\[current_year\]/g, String(currentYear.value))
				.replace(/\[current_month\]/g, String(currentMonth.value))
		: value;

const head_seo = computed(() => {
	const yoast = post.value?.yoast_head_json;

	if (!yoast) {
		return null;
	}

	const meta = [];
	const push = (key, content) => {
		if (content === undefined || content === null || content === "") return;
		meta.push({ ...key, content: String(content) });
	};

	push({ name: "description" }, yoast.description);
	push({ property: "og:locale" }, yoast.og_locale);
	push({ property: "og:type" }, yoast.og_type);
	push({ property: "og:title" }, applyDatePlaceholders(yoast.og_title));
	push({ property: "og:description" }, yoast.og_description);
	const ogUrl =
		typeof yoast.og_url === "string"
			? yoast.og_url.replace(
					$config.public.hostname,
					$config.public.current_hostname
			  )
			: yoast.og_url;
	push({ property: "og:url" }, ogUrl);
	push({ property: "og:site_name" }, yoast.og_site_name);
	push({ property: "article:published_time" }, yoast.article_published_time);
	push({ property: "article:modified_time" }, yoast.article_modified_time);

	if (Array.isArray(yoast.og_image) && yoast.og_image[0]) {
		const image = yoast.og_image[0];
		push({ property: "og:image" }, image.url);
		push({ property: "og:image:width" }, image.width);
		push({ property: "og:image:height" }, image.height);
		push({ property: "og:image:type" }, image.type);
	}

	push({ name: "author" }, yoast.author);
	push({ name: "twitter:card" }, yoast.twitter_card);
	push({ name: "twitter:creator" }, yoast.twitter_creator);
	push({ name: "twitter:site" }, yoast.twitter_site);

	return meta;
});

const addWatchListBox = () => {
	const watchList = `
		<div class="author-side-box">
			<div class="author-watchlist-subscription-ad">
				<div class="author-watchlist-image">
					<img
						width="340"
						height="224"
						src="/images/newsletter-image.911720f1.webp"
						alt="Post image"
						class="author-post-image-image"
						loading="lazy"
					/>
				</div>
				<div class="author-watchlist-information">
					<h4 class="author-watchlist-information-title">
						Get my after-hours watchlist, free
					</h4>
					<span class="author-watchlist-information-subtitle">
						The Night Tape, every trading day after the closing bell, plus The Second Session guide.
					</span>
					<form
						id="sb_form"
						accept-charset="UTF-8"
						action="https://traders.timothysykes.com/after-hours/night-tape-typ"
						method="GET"
						class="optin_form tim-sidebar-form"
						target="_blank"
					>
						<input
							id="inf_field_Email"
							name="email"
							type="email"
							placeholder="Enter Your Email"
							required
							class="infusion-field-input-container author-watchlist-information-email-input"
						/>
						<input
							name="funnel_id"
							type="hidden"
							value="ts_wl"
						/>
						<input
							name="page_id"
							type="hidden"
							value="night-tape-optin"
						/>
						<input
							name="page_type"
							type="hidden"
							value="optin"
						/>
						<input
							name="page_revision"
							type="hidden"
							value="A"
						/>
						<input
							name="action_id"
							type="hidden"
							value="opt_in"
						/>
						<button
							type="submit"
							name="submit"
							class="author-watchlist-information-subscribe-button"
						>
							Send me the Night Tape
						</button>
						<p>
							<label
								for="inf_field_Email"
								class="author-watchlist-information-description"
							>
								100% Privacy. I will never spam you!
							</label>
						</p>
					</form>
				</div>
			</div>
		</div>
  	`;
	return watchList;
};

const lastStockParsePostId = ref(null);

const contentFormatter = (content) => {
	if (!content) return "";

	const postId = post.value?.id;
	if (postId !== lastStockParsePostId.value) {
		stockSymbols.value = [];
		lastStockParsePostId.value = postId;
	}

	const $ = load(content, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	// const moreNewsList = `
	// 	<h4 class="mt-12 text-xl font-bold" style="margin-bottom: 24px;">
	// 		More Breaking News
	// 	</h4>
	// 	<ul class="list-disc mb-12">
	// 		${news_list.value
	// 			.map(
	// 				(item) =>
	// 					`<li style="margin-bottom: 24px;"> <a href="/news/${item.slug}/"> ${item.title} </a></li>`
	// 			)
	// 			.join("")}
	// 	</ul>
	// `;

	// const h3 = $("h3");
	// const h2 = $("h2");
	// const paragraphs = $("p");

	// if (h3.length > 1) {
	// 	const thirdH3 =
	// 		$("h3").eq(2).text() !== ""
	// 			? $("h3").eq(2)
	// 			: $("h3").eq(3).text() !== ""
	// 			? $("h3").eq(3)
	// 			: $("h2").eq(2);
	// 	const previousElement = thirdH3.prev();

	// 	if (previousElement.is("h2")) {
	// 		previousElement.before(moreNewsList);
	// 	} else {
	// 		thirdH3.before(moreNewsList);
	// 	}
	// } else if (h2.length > 1) {
	// 	h2.eq(2).prev().before(moreNewsList);
	// } else if (paragraphs.length > 10) {
	// 	const index = Math.floor(paragraphs.length / 2);
	// 	paragraphs.eq(index).after(moreNewsList);
	// }

	$("script").each((_index, scriptElement) => {
		if (
			$(scriptElement).attr("src") === "https://platform.twitter.com/widgets.js"
		) {
			$(scriptElement).remove();

			hasTwitterEmbedd.value = true;
		}

		if ($(scriptElement).attr("async")) {
			// Remove the async attribute
			$(scriptElement).removeAttr("async");

			// Add the defer attribute
			$(scriptElement).attr("defer", "");
		}
	});

	$("iframe").each((_index, iframeEl) => {
		if (!$(iframeEl).attr("loading")) {
			$(iframeEl).attr("loading", "lazy");
		}
		if (!$(iframeEl).attr("title")) {
			$(iframeEl).attr("title", $(iframeEl).attr("src"));
		}

		if (iframeEl.attribs.src.includes("displayprices=Candlestick")) {
			$(iframeEl).addClass("stockdio-candlestick");
		}
	});

	$("a").each((_index, el) => {
		if (
			el.attribs.href &&
			el.attribs.href.includes("https://content.timothysykes.com")
		) {
			el.attribs.href = el.attribs.href.replace(
				"https://content.timothysykes.com",
				$config.public.current_hostname.slice(0, -1)
			);
		}

		const regex = new RegExp(
			"\\b" + $config.public.hostname + "(?!\\.(jpg|jpeg|png|webp|pdf|gif)\\b)",
			"gi"
		);

		if (el.attribs.href && el.attribs.href.match(regex)) {
			const extracted_slug = el.attribs.href.split(".com/")[1];
			$(el).attr("href", $config.public.current_hostname + extracted_slug);
		}

		if (
			el.attribs.href &&
			!el.attribs.href.includes("#") &&
			$(el).find("img").length === 0
		) {
			$(el).addClass("external-link");
		}
	});

	$("#toc_container .toc_title").each((_index, toc_title) => {
		// const title = toc.querySelector('.toc_title');
		const button = $(`<button aria-label="Toggle Table of Contents">
      <span class="expand"> <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/><path d="M12.5 7.5V17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg></span>
      <span class="collapse">
        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/>
          <path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>

        </span>
      </button>`);

		$(toc_title).append(button);
	});

	$("img").each((_index, imgElement) => {
		const content = $config.public.hostname;
		let src = imgElement.attribs.src;
		let srcset = imgElement.attribs.srcset;
		const shouldProxy = ["production", "development"].includes(
			$config.public.env
		);

		src = normalizeMediaHostname(
			src.replace($config.public.hostname, content)
		);

		if (srcset) {
			if (shouldProxy) {
				srcset = rewriteSrcsetWithCfProxy(srcset);
			} else {
				// Still collapse any nested CF prefixes + normalize sister-site hosts
				srcset = srcset
					.split(/,\s+/)
					.map((part) => {
						const trimmed = part.trim();
						if (!trimmed) return "";
						const [url, ...rest] = trimmed.split(/\s+/);
						const fixed = normalizeMediaHostname(stripCfImageProxy(url));
						const descriptor = rest.join(" ");
						return descriptor ? `${fixed} ${descriptor}` : fixed;
					})
					.filter(Boolean)
					.join(", ");
			}
			$(imgElement).attr("srcset", srcset ?? "");
		}

		const proxiedSrc = shouldProxy ? applyCfImageProxy(src) : src;

		$(imgElement).attr("src", proxiedSrc);
		$(imgElement).attr("data-src", proxiedSrc);
	});

	$(".lyMe.hidef .pL").each((_index, ytElement) => {
		const ytSrc = ytElement.attribs["data-src"];
		const proxiedYtSrc = applyCfImageProxy(ytSrc);

		const image = `<img src="${proxiedYtSrc}" alt="${ytSrc}" class="yt-embed-image"  loading="lazy"/>`;

		$(ytElement).removeAttr("style");
		$(ytElement).removeAttr("data-src");
		$(".lyMe.hidef .pL").first().removeAttr("style");
		$(ytElement).append(image);
	});

	processHeadingsForReferencedStocks($, (stockSymbol) => {
		if (!stockSymbols.value.includes(stockSymbol)) {
			stockSymbols.value.push(stockSymbol);
		}
	});

	$("p").each((_, pElement) => {
		const text = $(pElement).text();

		const matches = text.match(STOCK_TICKER_REGEX);

		if (matches && matches[1]) {
			const stockSymbol = extractStockSymbol(text);
			if (!stockSymbols.value.includes(stockSymbol)) {
				stockSymbols.value.push(stockSymbol);
			}
		}
	});

	$("table.dynamic-table").each((_, tableElement) => {
		const $table = $(tableElement);
		const $tbody = $table.find("tbody");
		const $tr = $tbody.find("tr");

		$tr.each((_, trElement) => {
			const $td = $(trElement).find("td");
			const regex = /\b(NASDAQ|NYSE|AMEX|OTCPK)\b\s*:\s*([A-Z]+)\b/;
			let stockSymbol = null;

			// Search through all columns to find the ticker
			for (let i = 0; i < $td.length; i++) {
				const text = $td.eq(i).text().trim();
				const matches = text.match(regex);

				if (matches && matches[2]) {
					stockSymbol = matches[2].replace(/\s/g, "").toUpperCase();
					break;
				}
			}

			if (stockSymbol) {
				// Track the ticker so YTD data gets fetched for table-only symbols
				if (!stockSymbols.value.includes(stockSymbol)) {
					stockSymbols.value.push(stockSymbol);
				}

				// Set the data-stock-ticker attribute on the Performance (YTD) column (usually the 3rd column, index 2)
				if ($td.length > 2) {
					$td.eq(2).attr("data-stock-ticker", stockSymbol);
				}
			}
		});
	});

	const firstH3 = $("h3").first();

	const watchListContainer = $('<div id="WatchListContainer"></div>');
	watchListContainer.append(addWatchListBox());
	firstH3.after(watchListContainer);

	return $.html();
};

watch(
	beyondwords,
	(value) => {
		if (process.client && value?.id) {
			showBeyondWords.value = true;
		}
	},
	{ immediate: true }
);

onMounted(async () => {
	if (data.value?.redirected && process.client) {
		return;
	}

	if (process.client) {
		// Sidebar initialization is handled by the watcher above (supports direct page loads)

		await fetchLeftoverInformation();

		// TODO: Research why on Page Reload page scrolls to footer
		window.scrollTo(0, 0);

		if (post.value?.id) {
			store.changePostId(post.value.id);
		}

		if (post.value) {
			store.toggleLoading();
		}

		let counter = 0;

		const hostname = "https://content.timothysykes.com/";

		// Wait for content to load before parsing YouTube embeds
		// 250ms intervals, up to 10 retries (2.5 seconds total) for mobile devices
		const interval = setInterval(() => {
			counter += 1;
			const result = youtubeEmbedParser(hostname);
			if (result === "close" || counter >= 10) {
				clearInterval(interval);
			}
		}, 250);

		const beyondwords_podcast_id = post.value?.meta?.beyondwords_podcast_id;
		if (beyondwords.value?.id) {
			showBeyondWords.value = true;
		} else if (beyondwords_podcast_id) {
			const beyondwords_res = await fetchBeyondWords({
				podcastId: beyondwords_podcast_id
			});

			if (beyondwords_res?.id) {
				if (data.value) {
					data.value.beyondwords = beyondwords_res;
				}
				if (ssrData.value) {
					ssrData.value.beyondwords = beyondwords_res;
				}
				showBeyondWords.value = true;
			}
		}

		const stock_symbols_for_chart = document.querySelectorAll(
			`template[data-stock-symbol]`
		);

		if (stock_symbols_for_chart && stock_symbols_for_chart.length > 0) {
			stock_symbols_for_chart.forEach((element) => {
				const stock_symbol = element.getAttribute("data-stock-symbol");
				const payload = sttChartCreator(stock_symbol);

				element.insertAdjacentElement("beforebegin", payload.chartBody);
				element.insertAdjacentElement("beforebegin", payload.dependecyScript);
				element.insertAdjacentElement("beforebegin", payload.magicHappensScript);
			});
		}

		await checkForCookieAndSwipeUp();
	}
});

watch(content, async (newContent) => {
	if (process.client && newContent) {
		const toc_container = await document.querySelector("#toc_container");

		if (toc_container) {
			const button = toc_container.querySelector("button");
			const anchor_links = toc_container.querySelectorAll("li a");

			anchor_links.forEach((link) =>
				link.addEventListener("click", onClickOnAnchorScrollToTitle)
			);

			button.addEventListener("click", () => {
				const toc = toc_container.querySelector(".toc_list");

				if (toc.classList.contains("active")) {
					button.classList.remove("active");
					toc.classList.remove("active");
				} else {
					button.classList.add("active");
					toc.classList.add("active");
				}
			});
		}
	}
});

watch(hasTwitterEmbedd, async (newContent) => {
	if (process.client && newContent) {
		await setTimeout(addTwitterEmbedd, 1000);
	}
});

// eslint-disable-next-line require-await
watch(adPlacement, async (newContent, _) => {
	if (process.client && newContent) {
		if (process.client && newContent) {
			let counter = 0;

			const interval = setInterval(() => {
				const endOfArticleDiv = document.querySelector(".end-of-article");

				if (endOfArticleDiv) {
					clearInterval(interval);

					// Load and parse content using Cheerio
					const $ = load(endOfArticleDiv.innerHTML);
					$("p").each((_, element) => {
						const content = $(element).html();

						if (content.includes("[a href") && content.includes("[/a]")) {
							$(element).html(
								$(element)
									.html()
									.replace(/\[a\s+(.*?)\](.*?)\[(\/?)a\]/g, "<a $1>$2</a>")
							);
						}
					});

					endOfArticleDiv.innerHTML = $.html();
				} else {
					counter += 1;
					if (counter > 20) {
						clearInterval(interval);
					}
				}
			}, 200);
		}
	}
});

watch(
	stocks,
	(newData, _) => {
		if (newData && process.client) {
			Object.entries(newData).forEach(([_, stock]) => {
				const element = document.querySelector(
					`[data-stock-ticker="${stock.SYMBOL}"]`
				);

				if (element && stock.YTD_PCT != null) {
					element.setAttribute(
						"class",
						stock.YTD_PCT > 0 ? "symbol-status up" : "symbol-status down"
					);
					element.innerHTML = `${stock.YTD_PCT > 0 ? "+" : ""}${stock.YTD_PCT}%`;
				}
			});
		}
	},
	{ deep: true }
);

const mainImageFormated = computed(() => {
	if (!post.value) return null;

	if (post.value._embedded?.["wp:featuredmedia"]?.[0]?.source_url) {
		const sourceUrl =
			post.value._embedded["wp:featuredmedia"][0].source_url;

		if (!["development", "production"].includes($config.public.env)) {
			return {
				featuredImage: sourceUrl,
				lowRes: sourceUrl
			};
		}

		return {
			featuredImage: applyCfImageProxy(sourceUrl, {
				width: 740,
				quality: 90
			}),
			lowRes: applyCfImageProxy(sourceUrl, {
				width: 240,
				quality: 10
			})
		};
	}

	return null;
});

const post_category = computed(() => {
	if (!post.value?.categories?.[0]) return null;
	return post.value.categories[0];
});

const showReferencedStocks = computed(() => stockSymbols.value.length > 0);

const fallbackPost = {
	title: "Top 10 Most Successful Penny Stocks in History",
	text: `Everyone wants to know about the most successful penny stocks in history … They want to believe in the dream of getting filthy rich on cheap stocks.
They also want to buy big houses and expensive vehicles without doing all the hard work.`,
	url: "/blog/most-successful-penny-stocks-in-history/",
	image:
		"https://content.timothysykes.com/cdn-cgi/image/quality=90,format=webp,width=740/https://content.timothysykes.com/wp-content/uploads/2020/08/most-successful-penny-stocks-in-history.jpg"
};

useHead(() => ({
	script: [
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: schema_json.value
		}
	],
	link: [
		{
			rel: "canonical",
			href: `${current_hostname?.slice(0, -1) || ""}${route.path}`
		}
	]
}));

// Use useState to maintain consistent year/month between server and client
const currentYear = useState("currentYear", () => new Date().getFullYear());
const currentMonth = useState("currentMonth", () =>
	new Date().toLocaleString("en-US", { month: "long" })
);

const postTitle = computed(() => {
	if (!post.value?.title) return "";

	let title = post.value.title;
	if (title.includes("[current_year]") || title.includes("[current_month]")) {
		title = title
			.replace(/\[current_year\]/g, String(currentYear.value))
			.replace(/\[current_month\]/g, String(currentMonth.value));
		return decode(title);
	}

	return decode(title);
});
</script>

<template>
	<Head>
		<Title>
			{{ postTitle }}
		</Title>

		<template v-if="$config">
			<Link
				rel="preconnect"
				:href="`${current_hostname?.slice(0, -1)}`"
			/>
		</template>
	</Head>

	<Head v-if="head_seo">
		<template
			v-for="(tag, i) in head_seo"
			:key="i"
		>
			<Meta
				v-if="tag.name"
				:name="tag.name"
				:content="tag.content"
			/>
			<Meta
				v-else-if="tag.property"
				:property="tag.property"
				:content="tag.content"
			/>

			<Meta
				name="twitter:label1"
				content="Written by"
			/>
			<Meta
				v-if="author"
				name="twitter:data2"
				:content="author.name"
			/>
			<Meta
				name="twitter:label2"
				content="Est. reading time"
			/>
			<Meta
				v-if="post"
				name="twitter:data1"
				:content="`${calculateReadingTime(post.content)} minutes`"
			/>
		</template>
	</Head>

	<BlogContainer id="blog-page-container">
		<template #main>
			<ClientOnly>
				<PostSkeletonComponent v-if="isLoading" />
			</ClientOnly>

			<template v-if="!isLoading">
				<CategoryHeading
					v-if="post_category"
					:url="`/category/${post_category.slug}/`"
					:name="post_category.name"
					class="mb-[8px]"
				/>

				<Heading
					:text="post.title"
					tag="h1"
				/>

				<AuthorshipBlock
					v-if="post.author"
					class="mb-[12px]"
					v-bind="authorship"
					:author="{
						avatar: post.author.avatar,
						name: `${post.author.name ? post.author.name : 'Timothy Sykes'}`
					}"
				/>

				<DateReadingTime
					:date="post.modified || post.date_gmt"
					:reading-time="calculateReadingTime(post.content)"
				/>

				<section
					v-if="mainImageFormated"
					class="featured-image mb-[24px]"
				>
					<img
						:src="mainImageFormated.featuredImage"
						:data-src="mainImageFormated.featuredImage"
						:alt="`${post.title} Thumbnail`"
						class="w-full"
						width="740"
						height="532"
					/>
				</section>

				<SharePost :type="'primary'" />

				<LazyBeyondWordsIframe
					v-if="showBeyondWords && beyondwords && beyondwords.id"
					:beyondwords="beyondwords.id"
				/>

				<ClientOnly v-if="showReferencedStocks">
					<ReferencedStocks :stocks="stocks" />
				</ClientOnly>

				<section
					ref="content"
					class="content"
					v-html="contentFormatter(post.content)"
				/>

				<AizenEndOfArticleAd />

				<template v-if="tags && tags.length">
					<Divider />
					<div class="flex flex-wrap gap-[12px]">
						<img
							src="/images/tags-solid.svg"
							alt="tags-icon"
							height="24"
							width="24"
							class="tags-icon"
						/>
						<template
							v-for="(tag, index) in tags"
							:key="`lk-${index}`"
						>
							<div>
								<NuxtLink
									class="category-tag text-primary-500"
									:to="`/tag/${tag.slug}/`"
								>
									{{ tag.name }}
								</NuxtLink>
								<span
									v-if="index + 1 !== tags.length"
									:key="'sp-' + index"
									class="breadcrumb-caret"
								>
									,&nbsp;
								</span>
							</div>
						</template>
					</div>
				</template>

				<LazyFaqComponent
					v-if="faq"
					class="px-0"
					:route="$route.path"
					:faq="faq"
				/>

				<Divider />

				<RatingsComponent
					id="ratings-component"
					:post-id="post.id"
				/>

				<Divider />

				<section
					v-if="category && category.posts.nodes && category.posts.nodes.length > 0"
					id="related-posts"
				>
					<Heading
						tag="h4"
						:text="`Keep learning`"
						class="mb-[16px] font-semibold"
					/>

					<div class="flex flex-col gap-[24px]">
						<template
							v-for="(related_post, index) in category.posts.nodes"
							:key="related_post.uri"
						>
							<AizenRelatedPostAd v-if="index === 1" />

							<template v-if="route.path.includes(related_post.uri)">
								<RelatedPostCard
									:image="fallbackPost.image"
									:title="fallbackPost.title"
									:text="fallbackPost.text"
									:url="fallbackPost.url"
								/>
							</template>

							<RelatedPostCard
								v-else
								:image="
									related_post.featuredImage && related_post.featuredImage.node
										? related_post.featuredImage.node.sourceUrl
										: ''
								"
								:title="related_post.title"
								:text="related_post.excerpt"
								:url="related_post.uri"
							/>
						</template>
					</div>
				</section>
				<Divider id="comments-d" />

				<Comments
					:id="String(post.id)"
					:comments="comments"
				/>
			</template>
		</template>
		<template #aside>
			<ClientOnly>
				<PostSidebarSkeletonComponent v-if="isLoading" />
			</ClientOnly>
			<template v-if="!isLoading">
				<Divider class="lg:hidden" />

				<TimothyBioPreview
					class="mb-[32px]"
					:author-name="post.author.name ?? ''"
				/>

				<div class="lg:sticky lg:top-[92px]">
					<AizenSidebarAd />

					<ClientOnly v-if="showReferencedStocks && Object.keys(stocks).length > 0">
						<ReferencedStocksSidebar
							id="referenced-stocks-sidebar"
							:class="{ 'has-ad': !!aizenSidebarAd }"
							:stocks="stocks_ytd"
						/>
					</ClientOnly>
				</div>
			</template>
		</template>
	</BlogContainer>
</template>

<style lang="scss">
@import "@assets/utils.scss";
@import "@assets/blogPost.scss";
@import "@assets/watchListCTA.scss";

#referenced-stocks-sidebar {
	position: sticky;
	top: 92px;
	border-radius: 24px;
	&.has-ad {
		top: 564px;
	}
}

#ts-sidebar ~ #referenced-stocks-sidebar {
}

.referenced-symbols:not(#blog-page-container) {
	background-color: #f4f4f4;
}

#blog-page-container .referenced-symbols {
	background-color: #ffffff;
}
</style>
